// hooks/useModal.tsx
import moment from 'moment';
import React, { createContext, useContext, useState } from 'react';
import Dialog from '~/components/dialog';
import {
  Lancamento,
  ReprocessamentoFaturaCliente,
  ReprocessamentoFaturaDados,
  ReprocessamentoFaturaSolicitacaoDetalhes,
} from '~/models/reprocessamento-fatura';

type ModalProps = React.ComponentPropsWithoutRef<typeof Dialog>;

interface InvoiceReprocessContextData {
  clientData: ReprocessamentoFaturaCliente;
  invoiceData: ReprocessamentoFaturaDados[];
  setInvoiceData: React.Dispatch<
    React.SetStateAction<ReprocessamentoFaturaDados[] | null>
  >;
  setClientData: React.Dispatch<
    React.SetStateAction<ReprocessamentoFaturaCliente | null>
  >;
  solicitationData: ReprocessamentoFaturaSolicitacaoDetalhes & { id: string };
  setSolicitationData: React.Dispatch<
    React.SetStateAction<
      (ReprocessamentoFaturaSolicitacaoDetalhes & { id: string }) | null
    >
  >;
  newDate: string;
  setNewDate: React.Dispatch<React.SetStateAction<string>>;
  ticketNumber: string;
  setTicketNumber: React.Dispatch<React.SetStateAction<string>>;
  changeDate: boolean;
  setChangeDate: React.Dispatch<React.SetStateAction<boolean>>;
  clearData: () => void;
  selectedItems: Record<string, Lancamentos>;
  setSelectedItemsByInvoice: (invoiceId: string, item: Lancamento) => void;
}

const InvoiceReprocessContext = createContext<InvoiceReprocessContextData>(
  {} as InvoiceReprocessContextData,
);
type Lancamentos = ReprocessamentoFaturaDados['lancamentos'];
export const InvoiceReprocessProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [
    clientData,
    setClientData,
  ] = useState<ReprocessamentoFaturaCliente | null>(null);
  const [invoiceData, setInvoiceData] = useState<
    ReprocessamentoFaturaDados[] | null
  >(null);
  const [solicitationData, setSolicitationData] = useState<
    (ReprocessamentoFaturaSolicitacaoDetalhes & { id: string }) | null
  >(null);
  const [selectedItems, setSelectedItems] = useState<
    Record<string, Lancamentos>
  >(null);
  const [newDate, setNewDate] = useState(moment().format('DD/MM/YYYY'));
  const [ticketNumber, setTicketNumber] = useState('');
  const [changeDate, setChangeDate] = useState(false);

  const setSelectedItemsByInvoice = (invoiceId: string, item: Lancamento) => {
    setSelectedItems((prev) => {
      if (!prev) return { [invoiceId]: [item] };
      const currentItems = prev[invoiceId] || [];
      const exists = currentItems.includes(item);
      return {
        ...prev,
        [invoiceId]: exists
          ? currentItems.filter((i) => i !== item)
          : [...currentItems, item],
      };
    });
  };

  const clearData = () => {
    setClientData(null);
    setInvoiceData(null);
    setSolicitationData(null);
    setSelectedItems(null);

    setNewDate('');
    setChangeDate(false);
    setTicketNumber('');
  };
  return (
    <InvoiceReprocessContext.Provider
      value={{
        clientData,
        setClientData,
        invoiceData,
        setInvoiceData,
        solicitationData,
        setSelectedItems,
        clearData,
        setSolicitationData,
        newDate,
        setNewDate,
        setChangeDate,
        changeDate,
        selectedItems,
        setSelectedItemsByInvoice,
        ticketNumber,
        setTicketNumber,
      }}
    >
      {children}
    </InvoiceReprocessContext.Provider>
  );
};

export function useInvoiceReprocess() {
  return useContext(InvoiceReprocessContext);
}
